import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sequenceActionNext } from '@laramie/ui';
import { TIPO_PERSONA, TIPO_DOCUMENTO, TIPO_GEOREFERENCIA } from '../consts/entityType';
import { CloneObject } from '../utils/helpers';

/**
 * Mapea un domicilio ARCA al formato de dirección del sistema.
 */
function mapArcaDomicilio(dom) {
    return {
        idTipoGeoreferencia: TIPO_GEOREFERENCIA.OPEN_STREET_MAP,
        idPais: dom.idPaisLocal || 0,
        idProvincia: dom.idProvinciaLocal || 0,
        idLocalidad: dom.idLocalidadLocal || 0,
        calle: dom.calle || '',
        altura: dom.numero || '',
        piso: dom.piso || '',
        dpto: dom.dpto || '',
        codigoPostal: dom.codigoPostal || '',
    };
}

/**
 * Hook compartido para la integración ARCA en PersonaFisicaView y PersonaJuridicaView.
 *
 * Maneja el estado de arcaData, showArcaLookup, y provee handlers para:
 * - Pre-llenar formulario con datos de ARCA
 * - Crear documento CUIT principal
 * - Mapear domicilios
 * - Determinar qué campos están pre-llenados (disabled)
 *
 * @param {Object} config
 * @param {boolean} config.isNew - true si el modo es NEW
 * @param {'FISICA'|'JURIDICA'} config.tipoPersonaEsperado
 * @param {Function} config.mapearFormValues - (persona) => { campo: valor }
 * @param {Function} config.mapearDomicilios - (persona, entity, idDocumento) => entity modificado; idDocumento es el
 *   id temporal para otro documento de ARCA (el DNI de la física)
 * @param {Function} config.camposArca - (arcaData) => { campo: boolean }
 * @param {*} config.formValues - valores actuales del form (useForm)
 * @param {Function} config.formSet - setter del form (useForm)
 * @param {Function} config.setState - setter del entity state
 * @param {Function} config.setPendingChange
 * @param {Function} [config.onPostProcess] - callback opcional post-prefill (ej: mapear formaJuridica)
 */
export default function useArcaIntegration({
    isNew,
    tipoPersonaEsperado,
    mapearFormValues,
    mapearDomicilios,
    camposArca,
    formValues, formSet, setState, setPendingChange,
    onPostProcess,
}) {
    const [arcaData, setArcaData] = useState(null);
    const [showArcaLookup, setShowArcaLookup] = useState(isNew);

    const dispatch = useDispatch();
    const sequenceValue = useSelector( (state) => state.sequence.value );

    const idTipoPersona = tipoPersonaEsperado === 'FISICA' ? TIPO_PERSONA.FISICA : TIPO_PERSONA.JURIDICA;

    function handlePersonaFound(persona) {
        // Tipo persona y CUIT ya validados por ArcaLookup antes de llegar acá
        setArcaData(persona);
        setShowArcaLookup(false);
        setPendingChange(true);

        const updates = {
            ...mapearFormValues(persona),
            idTipoDocumento: TIPO_DOCUMENTO.CUIT,
            numeroDocumento: persona.cuit,
        };
        const updatedForm = { ...formValues, ...updates };
        formSet(updatedForm);

        // Los ids temporales de los documentos de ARCA salen de la secuencia, como los de las grillas: un documento
        // cargado a mano después toma otro id y se suma a los de ARCA en vez de reemplazar al que tenía el mismo
        const idCuit = -(sequenceValue || 1);
        dispatch(sequenceActionNext());
        dispatch(sequenceActionNext());

        setState(prevState => {
            let entity = CloneObject(prevState.entity);
            if (entity.persona && 'origen' in entity.persona) {
                entity.persona.origen = 'ARCA';
            }
            entity.documentos = [{
                id: idCuit,
                idTipoPersona: idTipoPersona,
                idPersona: 0,
                idTipoDocumento: TIPO_DOCUMENTO.CUIT,
                numeroDocumento: persona.cuit,
                principal: true,
                state: 'a'
            }];
            entity = mapearDomicilios(persona, entity, idCuit - 1);
            return { ...prevState, entity };
        });

        if (onPostProcess) onPostProcess(persona, updatedForm);
    }

    function handleSkip() {
        setShowArcaLookup(false);
    }

    function isArcaField(field) {
        if (!arcaData) return false;
        const fields = camposArca(arcaData);
        return fields[field] || false;
    }

    return { arcaData, showArcaLookup, handlePersonaFound, handleSkip, isArcaField };
}

export { mapArcaDomicilio };
